/**
 * Pure Web-Mercator math for MapVisual: no network, no DOM, no React.
 * Fits `2 * radiusMiles` into a design-space pixel box (clamped zoom 6-14),
 * and lays out a small (<=3x3) grid of standard 256px OSM raster tiles
 * centered on a lat/lng at that zoom.
 */

export const TILE_SIZE = 256;
export const MIN_ZOOM = 6;
export const MAX_ZOOM = 14;
export const DEFAULT_RADIUS_MILES = 25;
export const MAX_TILE_GRID = 3;

const EARTH_CIRCUMFERENCE_METERS = 40_075_016.686;
const BASE_METERS_PER_PIXEL = EARTH_CIRCUMFERENCE_METERS / TILE_SIZE; // meters/px at zoom 0, equator
const MILES_TO_METERS = 1609.344;

function clampZoom(zoom: number): number {
  return Math.min(MAX_ZOOM, Math.max(MIN_ZOOM, zoom));
}

/** Ground resolution in meters/pixel at a given zoom + latitude (Web Mercator). */
export function metersPerPixel(zoom: number, latitude: number): number {
  return (BASE_METERS_PER_PIXEL * Math.cos((latitude * Math.PI) / 180)) / 2 ** zoom;
}

/**
 * The integer zoom (6-14) at which `2 * radiusMiles` (the coverage diameter)
 * fits inside the smaller dimension of a `boxWidthPx x boxHeightPx` box.
 */
export function fitZoom(
  radiusMiles: number,
  latitude: number,
  boxWidthPx: number,
  boxHeightPx: number,
): number {
  const diameterMeters = Math.max(2 * radiusMiles, 0.1) * MILES_TO_METERS;
  const boxPx = Math.max(1, Math.min(boxWidthPx, boxHeightPx));
  const cosLat = Math.max(Math.cos((latitude * Math.PI) / 180), 0.01);
  // boxPx * metersPerPixel(z) >= diameterMeters, metersPerPixel(z) = BASE*cosLat/2^z
  // => 2^z <= BASE*cosLat*boxPx/diameterMeters
  const maxScale = (BASE_METERS_PER_PIXEL * cosLat * boxPx) / diameterMeters;
  return clampZoom(Math.floor(Math.log2(Math.max(maxScale, 1))));
}

/** On-screen pixel length of `miles` at a given zoom + latitude. */
export function milesToPixels(miles: number, zoom: number, latitude: number): number {
  return (Math.max(miles, 0) * MILES_TO_METERS) / metersPerPixel(zoom, latitude);
}

/** Standard slippy-map tile <-> world-pixel projection (EPSG:3857). */
export function worldPixel(lat: number, lng: number, zoom: number): { x: number; y: number } {
  const scale = TILE_SIZE * 2 ** zoom;
  const x = ((lng + 180) / 360) * scale;
  const clampedLat = Math.min(85.05112878, Math.max(-85.05112878, lat));
  const sinLat = Math.sin((clampedLat * Math.PI) / 180);
  const y = (0.5 - Math.log((1 + sinLat) / (1 - sinLat)) / (4 * Math.PI)) * scale;
  return { x, y };
}

export interface MapTile {
  tileX: number;
  tileY: number;
  zoom: number;
  /** Pixel offset within the tile grid container (not the outer box). */
  left: number;
  top: number;
}

export interface OsmTileLayout {
  zoom: number;
  tiles: MapTile[];
  gridSize: number;
  /** Pixel offset (design-space) to translate the tile grid so lat/lng lands at the box center. */
  offsetX: number;
  offsetY: number;
}

/**
 * Lays out a square grid of OSM tiles (never more than 3x3) covering
 * `boxWidthPx x boxHeightPx` centered on `lat`/`lng`, at the zoom `fitZoom`
 * picks for `radiusMiles`. Horizontal tile indices wrap at the antimeridian;
 * vertical indices clamp (no polar wrap).
 */
export function osmTileLayout(
  lat: number,
  lng: number,
  radiusMiles: number,
  boxWidthPx: number,
  boxHeightPx: number,
): OsmTileLayout {
  const zoom = fitZoom(radiusMiles, lat, boxWidthPx, boxHeightPx);
  const world = worldPixel(lat, lng, zoom);
  const centerTileX = Math.floor(world.x / TILE_SIZE);
  const centerTileY = Math.floor(world.y / TILE_SIZE);

  const tilesAcross = Math.ceil(boxWidthPx / TILE_SIZE) + 1;
  const tilesDown = Math.ceil(boxHeightPx / TILE_SIZE) + 1;
  const gridSize = Math.min(MAX_TILE_GRID, Math.max(1, Math.max(tilesAcross, tilesDown)));
  const half = Math.floor(gridSize / 2);

  const tileCount = 2 ** zoom;
  const tiles: MapTile[] = [];
  for (let dy = -half; dy <= half; dy++) {
    for (let dx = -half; dx <= half; dx++) {
      const rawX = centerTileX + dx;
      const tileX = ((rawX % tileCount) + tileCount) % tileCount;
      const tileY = Math.min(tileCount - 1, Math.max(0, centerTileY + dy));
      tiles.push({
        tileX,
        tileY,
        zoom,
        left: (dx + half) * TILE_SIZE,
        top: (dy + half) * TILE_SIZE,
      });
    }
  }

  const worldOriginX = (centerTileX - half) * TILE_SIZE;
  const worldOriginY = (centerTileY - half) * TILE_SIZE;

  return {
    zoom,
    tiles,
    gridSize,
    offsetX: boxWidthPx / 2 - (world.x - worldOriginX),
    offsetY: boxHeightPx / 2 - (world.y - worldOriginY),
  };
}
